import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Mail, Phone, Send, Sparkles, CheckCircle2, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useCmsSection } from '../hooks/useCmsContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_MSG = 1000;

// Hardcoded fallbacks — the public page renders these when the CMS payload
// hasn't arrived yet (first paint) or the API is unreachable. They MUST stay
// in sync with CMS_DEFAULTS["contact"] in backend/server.py.
const CONTACT_FALLBACK = {
  page_eyebrow: 'Say hello',
  page_title: 'Questions before the big day?',
  page_intro: "Whether you're juggling allergy needs, curious about a specific craft, or just want to know what it feels like at one of our parties — we'd love to chat.",
  page_reply_note: "We typically reply within one business day. Weekends? We're usually at a party — check back Monday.",
  phone: '905-616-7617',
  phone_tel: '+19056167617',
  email: 'info@creativebirthdays.ca',
  address: { street: '1500 Upper Middle Road West, Unit 4', city: 'Oakville', province: 'ON', postal_code: 'L6M 3G3' },
  hours_subtitle: "We're open 7 days a week.",
  hours_note: "Last party booking starts 2 hours before closing.",
  hours: [
    { label: 'Mon – Fri', open: '9 AM', close: '7 PM', closed: false },
    { label: 'Sat – Sun', open: '10 AM', close: '6 PM', closed: false },
  ],
};

// Canadian-friendly phone validation: accepts any format with ≥10 digits.
const validPhone = (raw) => (raw || '').replace(/\D+/g, '').length >= 10;
const validEmail = (raw) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(raw || '');

export const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', consented: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Pull the contact block from the CMS. Fallback keeps the page looking
  // exactly like before if the API is slow/down.
  const contact = useCmsSection('contact', CONTACT_FALLBACK);
  const addr = contact.address || CONTACT_FALLBACK.address;
  // Build a display-ready address + Google-Maps query URL so the "View on map"
  // links follow whatever the admin saves.
  const addrLine1 = addr.street || '';
  const addrLine2 = [addr.city, addr.province, addr.postal_code].filter(Boolean).join(addr.province ? ' ' : ', ').replace(/^,/, '').trim();
  const addrLine2Pretty = [addr.city, `${addr.province || ''} ${addr.postal_code || ''}`.trim()].filter(Boolean).join(', ');
  const mapsQuery = encodeURIComponent([addrLine1, addr.city, addr.province, addr.postal_code].filter(Boolean).join(', '));

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!validEmail(form.email)) errs.email = 'Enter a valid email';
    if (!validPhone(form.phone)) errs.phone = 'Enter a valid phone (10 digits)';
    if (!form.message.trim()) errs.message = 'Please write a quick note';
    else if (form.message.length > MAX_MSG) errs.message = `Max ${MAX_MSG} characters`;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/contact`, form);
      // Remember the prospect so the landing page can greet them on return visits
      try {
        localStorage.setItem('cb_prospect', JSON.stringify({
          inquiry_id: res.data?.inquiry_id,
          name: form.name,
          saved_at: Date.now(),
        }));
      } catch { /* private browsing — ignore */ }
      setSuccess(true);
      setForm({ name: '', email: '', phone: '', message: '', consented: false });
      toast.success('Thanks! We\'ll be in touch within one business day.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />

      <div className="max-w-5xl mx-auto px-4 py-10 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-5 gap-8">
          {/* LEFT: Pitch */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-[#FF3B6B]" />
              <p className="text-sm tracking-wide text-[#6B6B7A] uppercase">{contact.page_eyebrow || 'Say hello'}</p>
            </div>
            <h1 className="text-4xl sm:text-5xl font-semibold text-[#1E1E24] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {contact.page_title || 'Questions before the big day?'}
            </h1>
            <p className="text-[#6B6B7A] mb-6 leading-relaxed">
              {contact.page_intro || CONTACT_FALLBACK.page_intro}
            </p>
            <div className="space-y-3 text-sm text-[#1E1E24]">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#FF3B6B]" />
                <a href={`mailto:${contact.email}`} className="hover:text-[#FF3B6B]" data-testid="contact-email">{contact.email}</a>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#FF3B6B]" />
                <a href={`tel:${contact.phone_tel || contact.phone || ''}`} className="hover:text-[#FF3B6B]" data-testid="contact-phone">{contact.phone}</a>
              </div>
              {contact.page_reply_note && (
                <p className="text-[#6B6B7A] text-xs leading-relaxed pt-4">
                  {contact.page_reply_note}
                </p>
              )}
            </div>
          </div>

          {/* RIGHT: Form */}
          <div className="md:col-span-3">
            {success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-3xl border border-[#E8E5DF] p-8 sm:p-10 shadow-sm text-center"
                data-testid="contact-success"
              >
                <CheckCircle2 className="w-14 h-14 text-[#06D6A0] mx-auto mb-4" />
                <h2 className="text-2xl font-semibold text-[#1E1E24] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Message received!
                </h2>
                <p className="text-[#6B6B7A] mb-6">
                  A real human from our team will reply within one business day. In the meantime, check your inbox for our auto-reply with a link to packages.
                </p>
                <Button
                  onClick={() => setSuccess(false)}
                  variant="outline"
                  className="rounded-full border-[#E8E5DF]"
                  data-testid="contact-send-another"
                >
                  Send another message
                </Button>
              </motion.div>
            ) : (
              <form
                onSubmit={submit}
                className="bg-white rounded-3xl border border-[#E8E5DF] p-6 sm:p-8 shadow-sm space-y-5"
                data-testid="contact-form"
              >
                <div>
                  <Label htmlFor="contact-name" className="text-sm font-medium text-[#1E1E24]">Full name</Label>
                  <Input
                    id="contact-name"
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    placeholder="Jane Parent"
                    className={`mt-1 rounded-xl ${errors.name ? 'border-[#FF3B6B]' : 'border-[#E8E5DF]'}`}
                    data-testid="contact-input-name"
                    maxLength={120}
                  />
                  {errors.name && <p className="text-xs text-[#FF3B6B] mt-1" data-testid="contact-error-name">{errors.name}</p>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact-email" className="text-sm font-medium text-[#1E1E24]">Email</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                      placeholder="jane@example.com"
                      className={`mt-1 rounded-xl ${errors.email ? 'border-[#FF3B6B]' : 'border-[#E8E5DF]'}`}
                      data-testid="contact-input-email"
                    />
                    {errors.email && <p className="text-xs text-[#FF3B6B] mt-1" data-testid="contact-error-email">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="contact-phone" className="text-sm font-medium text-[#1E1E24]">Phone</Label>
                    <Input
                      id="contact-phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => update('phone', e.target.value)}
                      placeholder="(416) 555-0100"
                      className={`mt-1 rounded-xl ${errors.phone ? 'border-[#FF3B6B]' : 'border-[#E8E5DF]'}`}
                      data-testid="contact-input-phone"
                    />
                    {errors.phone && <p className="text-xs text-[#FF3B6B] mt-1" data-testid="contact-error-phone">{errors.phone}</p>}
                  </div>
                </div>

                <div>
                  <Label htmlFor="contact-message" className="text-sm font-medium text-[#1E1E24]">Your question</Label>
                  <Textarea
                    id="contact-message"
                    value={form.message}
                    onChange={(e) => update('message', e.target.value.slice(0, MAX_MSG))}
                    placeholder="Tell us about your party — date, favourite crafts, allergy needs, anything."
                    rows={5}
                    className={`mt-1 rounded-xl ${errors.message ? 'border-[#FF3B6B]' : 'border-[#E8E5DF]'}`}
                    data-testid="contact-input-message"
                  />
                  <div className="flex justify-between mt-1">
                    {errors.message ? (
                      <p className="text-xs text-[#FF3B6B]" data-testid="contact-error-message">{errors.message}</p>
                    ) : <span />}
                    <p className="text-xs text-[#6B6B7A]">{form.message.length} / {MAX_MSG}</p>
                  </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer" htmlFor="contact-consent">
                  <Checkbox
                    id="contact-consent"
                    checked={form.consented}
                    onCheckedChange={(v) => update('consented', !!v)}
                    data-testid="contact-consent-checkbox"
                  />
                  <span className="text-sm text-[#6B6B7A] leading-snug">
                    It's OK to email me occasionally about promotions & new packages. (CASL/GDPR — you can unsubscribe any time.)
                  </span>
                </label>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full py-6 text-base"
                  data-testid="contact-submit-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? 'Sending…' : 'Send Inquiry'}
                </Button>
              </form>
            )}
          </div>
        </motion.div>

        {/* Visit us — address + map + hours */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.4 }}
          className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-5 gap-8"
          data-testid="contact-visit-us"
        >
          {/* Info cards column */}
          <div className="md:col-span-2 space-y-4">
            {/* Address card */}
            <div
              className="rounded-3xl p-6 border border-[#E8E5DF]"
              style={{ background: 'linear-gradient(135deg, #FFF4D6 0%, #FFE9EE 100%)' }}
              data-testid="contact-address-card"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center flex-none shadow-sm">
                  <MapPin className="w-5 h-5 text-[#FF3B6B]" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Visit our studio
                  </h3>
                  <p className="text-sm text-[#1E1E24] mt-1 leading-relaxed" data-testid="contact-address-text">
                    {addrLine1}<br/>
                    {addrLine2Pretty || addrLine2}
                  </p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-block text-xs font-semibold text-[#FF3B6B] hover:underline mt-2"
                    data-testid="contact-directions-link"
                  >
                    Get directions →
                  </a>
                </div>
              </div>
            </div>

            {/* Operating hours card */}
            <div
              className="rounded-3xl p-6 border border-[#E8E5DF]"
              style={{ background: 'linear-gradient(135deg, #FFF4D6 0%, #FFE9EE 100%)' }}
              data-testid="contact-hours-card"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center flex-none shadow-sm">
                  <Clock className="w-5 h-5 text-[#FF3B6B]" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Operating hours
                  </h3>
                  <p className="text-xs text-[#6B6B7A] mt-0.5">{contact.hours_subtitle || CONTACT_FALLBACK.hours_subtitle}</p>
                  <dl className="mt-3 text-sm space-y-1.5 text-[#1E1E24]" data-testid="contact-hours-list">
                    {(contact.hours || CONTACT_FALLBACK.hours).map((h, i) => (
                      <div key={i} className="flex items-center justify-between gap-3">
                        <dt className="text-[#6B6B7A]">{h.label}</dt>
                        <dd className="font-semibold">
                          {h.closed ? 'Closed' : `${h.open || ''}${h.open && h.close ? ' – ' : ''}${h.close || ''}`}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  {contact.hours_note && (
                    <p className="text-[11px] text-[#6B6B7A] mt-3 italic border-t border-dashed border-[#E8E5DF] pt-2">
                      {contact.hours_note}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Embedded map */}
          <div className="md:col-span-3 rounded-3xl overflow-hidden border border-[#E8E5DF] shadow-sm" data-testid="contact-map">
            <iframe
              title="Creative Birthdays Oakville location map"
              src={`https://www.google.com/maps?q=${mapsQuery}&output=embed`}
              width="100%"
              height="100%"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen=""
              style={{ border: 0, minHeight: 360, display: 'block' }}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
